import React from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Briefcase, Clock, TrendingDown } from 'lucide-react';
import { Badge } from '../ui/badge';
import { API } from '../governance_v2/constants';
import { PROJECT_STATUS, VERDICT, fmtDate } from './constants';
import { SCHEDULE_STATUS } from './PvAvFPanel';

const Card = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700" data-testid={testid}>
    <Icon className={`h-5 w-5 ${tone}`} /><p className="mt-2 text-2xl font-bold">{value}</p><p className="text-xs text-slate-500">{label}</p>
  </div>
);

export const PortfolioPanel = ({ companyId, onOpen }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['portfolio', companyId],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/portfolio`)).data,
    enabled: !!companyId,
  });
  if (isLoading || !data) return <p className="text-sm text-slate-400">Calculando cartera…</p>;
  const s = data.summary;
  return (
    <div className="space-y-4" data-testid="portfolio-panel">
      <div className="grid gap-3 sm:grid-cols-4">
        <Card icon={Briefcase} label={`proyectos activos de ${s.projects}`} value={s.active} tone="text-sky-500" testid="portfolio-active" />
        <Card icon={AlertTriangle} label="atrasados (SPI o CPM)" value={s.late} tone="text-red-500" testid="portfolio-late" />
        <Card icon={Clock} label="en riesgo" value={s.at_risk} tone="text-amber-500" testid="portfolio-at-risk" />
        <Card icon={TrendingDown} label="KPIs que no alcanzan su meta" value={s.kpis_off_track} tone="text-violet-500" testid="portfolio-kpis-off" />
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm" data-testid="portfolio-table">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50">
            <tr><th className="p-3">Proyecto</th><th className="p-3">Plan / Actual</th><th className="p-3">SPI</th><th className="p-3" title="Fecha fin comprometida del proyecto → fin calculado por ruta crítica → fin proyectado al ritmo actual">Fin comprometido → CPM → Forecast</th><th className="p-3">Ruta crítica</th><th className="p-3">KPIs</th></tr>
          </thead>
          <tbody>{data.items.map((p) => (
            <tr key={p.id} onClick={() => onOpen(p.id)} className="cursor-pointer border-t border-slate-200 transition-colors duration-150 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/40" data-testid={`portfolio-row-${p.id}`}>
              <td className="p-3"><p className="font-medium">{p.title}</p><Badge className={PROJECT_STATUS[p.status]?.cls}>{PROJECT_STATUS[p.status]?.label}</Badge></td>
              <td className="p-3">{p.planned_pct}% / <b>{p.actual_pct}%</b></td>
              <td className="p-3"><Badge className={SCHEDULE_STATUS[p.schedule_status].cls}>{p.spi ?? '—'} · {SCHEDULE_STATUS[p.schedule_status].label}</Badge></td>
              <td className="p-3 text-xs">{fmtDate(p.end_date)} → <span className={p.cpm_alert ? 'font-semibold text-red-600 dark:text-red-400' : ''}>{fmtDate(p.cpm_finish)}</span> → {fmtDate(p.forecast_finish)}</td>
              <td className="max-w-xs p-3 text-xs text-slate-500">{p.critical_path.join(' → ') || '—'}</td>
              <td className="p-3"><div className="flex flex-wrap gap-1">{p.predictions.map((k, i) => (
                <Badge key={i} className={k.will_meet === false ? SCHEDULE_STATUS.atrasado.cls : VERDICT[k.verdict]?.cls}>{k.kpi_name}</Badge>))}</div></td>
            </tr>
          ))}</tbody>
        </table>
        {!data.items.length && <p className="p-6 text-center text-sm text-slate-500">Sin proyectos.</p>}
      </div>
    </div>
  );
};
